export type ModelProviderName =
  "deepseek" | "gemini" | "openai" | "claude" | "local";

export type ModelCapability =
  | "conversation"
  | "planning"
  | "summarization"
  | "classification"
  | "tool-intent"
  | "coding";

export type ModelProviderConfig = {
  provider: ModelProviderName;
  model: string;
  enabled: boolean;
  connected: boolean;
  purpose: string;
  capabilities: ModelCapability[];
  contextLimit: number;
  cost: {
    inputPerMillion: number;
    outputPerMillion: number;
    currency: "USD";
  };
  latency: {
    expectedMs: number;
  };
  requestPolicy:
    "deterministic-local-only" | "external-disabled" | "server-side-external";
  dataPolicy: "no-secrets" | "public-context-only" | "governed-project-context";
  health: "healthy" | "disabled" | "unknown";
  fallbackPriority: number;
};

export interface ModelProvider {
  config: ModelProviderConfig;

  complete(prompt: string): Promise<{
    text: string;
    provider: ModelProviderName;
    model: string;
  }>;
}

export interface ModelRouter {
  select(capability: ModelCapability): {
    provider: ModelProviderConfig;
    reason: string;
  };
}

export class DeterministicModelProvider implements ModelProvider {
  config: ModelProviderConfig = {
    provider: "local",
    model: "deterministic-cyan-planner",
    enabled: true,
    connected: true,
    purpose:
      "Deterministic Cyan provider for tests and offline-safe execution.",
    capabilities: [
      "conversation",
      "planning",
      "summarization",
      "classification",
      "tool-intent",
      "coding",
    ],
    contextLimit: 12000,
    cost: {
      inputPerMillion: 0,
      outputPerMillion: 0,
      currency: "USD",
    },
    latency: {
      expectedMs: 1,
    },
    requestPolicy: "deterministic-local-only",
    dataPolicy: "no-secrets",
    health: "healthy",
    fallbackPriority: 1000,
  };

  async complete(prompt: string) {
    return {
      text: `Deterministic response for: ${prompt.slice(0, 120)}`,
      provider: this.config.provider,
      model: this.config.model,
    };
  }
}

export type ModelRouterOptions = {
  allowDeterministicFallback?: boolean;
};

export class StaticModelRouter implements ModelRouter {
  constructor(
    private readonly providers: ModelProviderConfig[] = [
      new DeterministicModelProvider().config,
    ],
    private readonly options: ModelRouterOptions = {
      allowDeterministicFallback: true,
    },
  ) {}

  select(capability: ModelCapability) {
    const candidates = [...this.providers]
      .filter(
        (provider) =>
          provider.enabled &&
          provider.connected &&
          provider.health !== "disabled" &&
          provider.capabilities.includes(capability),
      )
      .sort((left, right) => left.fallbackPriority - right.fallbackPriority);

    const allowedCandidates =
      this.options.allowDeterministicFallback === false
        ? candidates.filter((provider) => provider.provider !== "local")
        : candidates;

    const provider = allowedCandidates[0];

    if (!provider) {
      if (this.options.allowDeterministicFallback === false) {
        throw new Error(`No enabled external model provider for ${capability}`);
      }

      throw new Error(`No enabled model provider for ${capability}`);
    }

    return {
      provider,
      reason:
        provider.provider === "local"
          ? "Selected deterministic local provider."
          : `Selected external provider ${provider.provider} through governed server-side routing.`,
    };
  }
}
